import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
    ArrowLeft,
    Box,
    UserCircle,
    Calendar,
    Save,
    Signature,
    CheckCircle2,
    FileText,
    Image as ImageIcon,
    Upload,
    X
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Input } from '../../components/ui/input';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";
import api from '../../utils/api';
import { Asset } from '../../types/asset';
import { User, Department } from '../../types';
import { toast } from 'sonner';
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import AccessRestricted from '../../components/AccessRestricted';

interface InspectionTemplateItem {
    id: number;
    question: string;
    listOrder: number;
    isMandatory: boolean;
}

interface InspectionTemplate {
    id: number;
    name: string;
    assetType: string;
    description: string;
    isDefault: boolean;
    isActive: boolean;
    items: InspectionTemplateItem[];
}

const currentTimeHHMM = () => {
    const now = new Date();
    return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
};

/**
 * AU suburb list for the Location (Suburb) picker — one static asset served by the app
 * (client/public/au-suburbs.json), filtered in memory. A network round-trip per keystroke
 * was unusable (public suburb endpoints answer in seconds), and the list never changes
 * between deploys, so it is fetched once and reused. Row shape: [name, state, postcode].
 */
type SuburbRow = [name: string, state: string, postcode: string];

const MAX_SUBURB_SUGGESTIONS = 8;
const SUBURBS_URL = `${process.env.PUBLIC_URL || ''}/au-suburbs.json`;

let suburbsPromise: Promise<SuburbRow[]> | null = null;
const loadSuburbs = (): Promise<SuburbRow[]> => {
    if (!suburbsPromise) {
        suburbsPromise = fetch(SUBURBS_URL)
            .then((res) => {
                if (!res.ok) throw new Error(`Suburb list failed (${res.status})`);
                return res.json();
            })
            .then((rows: SuburbRow[]) => (Array.isArray(rows) ? rows : []))
            // A failed load must not be cached, or the field stays dead for the session.
            .catch((error) => {
                suburbsPromise = null;
                throw error;
            });
    }
    return suburbsPromise;
};

const NewInspection: React.FC = () => {
    const ability = useAbility(AbilityContext);
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();

    const [asset, setAsset] = useState<Asset | null>(null);
    const [template, setTemplate] = useState<InspectionTemplate | null>(null);
    const [allTemplates, setAllTemplates] = useState<InspectionTemplate[]>([]);
    const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    // Form State
    const [inspectionDate, setInspectionDate] = useState(new Date().toISOString().split('T')[0]);
    const [inspectionTime, setInspectionTime] = useState(() => currentTimeHHMM());
    const [inspectorName, setInspectorName] = useState('');
    const [suburb, setSuburb] = useState('');
    const [outcome, setOutcome] = useState<'Pass' | 'Pass with Notes' | 'Fail'>('Pass');
    const [generalComments, setGeneralComments] = useState('');

    // Suburb search (Location) state
    const [suburbSuggestions, setSuburbSuggestions] = useState<{ label: string; value: string }[]>([]);
    const [suburbSearching, setSuburbSearching] = useState(false);
    const [showSuburbSuggestions, setShowSuburbSuggestions] = useState(false);
    const suburbBoxRef = useRef<HTMLDivElement>(null);

    // Answers State
    const [answers, setAnswers] = useState<Record<number, string>>({});
    const [comments, setComments] = useState<Record<number, string>>({});

    // Signature
    const [signatureType, setSignatureType] = useState<'Draw' | 'Type'>('Type');
    const [signatureData, setSignatureData] = useState('');
    const [typedSignature, setTypedSignature] = useState('');
    const [isDrawing, setIsDrawing] = useState(false);
    const canvasRef = useRef<HTMLCanvasElement>(null);

    // Photos
    const [photos, setPhotos] = useState<File[]>([]);
    const [existingPhotos, setExistingPhotos] = useState<string[]>([]);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const selected = Array.from(e.target.files);
            const validFiles = selected.filter(f => f.size <= 5 * 1024 * 1024);
            if (validFiles.length < selected.length) {
                toast.error("Some files were skipped as they exceed 5MB");
            }
            setPhotos(prev => [...prev, ...validFiles]);
        }
    };

    const removePhoto = (index: number) => {
        setPhotos(prev => prev.filter((_, i) => i !== index));
    };

    const removeExistingPhoto = (index: number) => {
        setExistingPhotos(prev => prev.filter((_, i) => i !== index));
    };

    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const draftId = queryParams.get('draft');

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                // Fetch asset
                const assetRes = await api.get<Asset>(`/api/assets/${id}`);
                setAsset(assetRes);

                // Fetch all templates for asset type
                const templatesRes = await api.get<InspectionTemplate[]>(`/api/inspection-templates?assetType=${encodeURIComponent(assetRes.type)}`);

                if (templatesRes && templatesRes.length > 0) {
                    // Store all active templates
                    const activeTemplates = templatesRes.filter((t) => t.isActive !== false);
                    setAllTemplates(activeTemplates);

                    // Find the default template, fall back to the first one
                    const defaultTemplate = activeTemplates.find((t) => t.isDefault) || activeTemplates[0];
                    setSelectedTemplateId(defaultTemplate.id);

                    const templateDetailRes = await api.get<InspectionTemplate>(`/api/inspection-templates/${defaultTemplate.id}`);
                    setTemplate(templateDetailRes);

                    const defaultAns: Record<number, string> = {};
                    templateDetailRes.items.forEach((item: any) => {
                        defaultAns[item.id] = '';
                    });

                    let loadedDraft = false;

                    // If draft ID provided, fetch it
                    if (draftId) {
                        const inspections = await api.get<any[]>(`/api/assets/${id}/inspections`);
                        const draft = inspections.find(i => i.id === parseInt(draftId));
                        if (draft) {
                            if (draft.inspectionDate) setInspectionDate(new Date(draft.inspectionDate).toISOString().split('T')[0]);
                            if (draft.inspectorName) setInspectorName(draft.inspectorName);
                            if (draft.outcome) setOutcome(draft.outcome);
                            if (draft.generalComments) setGeneralComments(draft.generalComments);
                            if (draft.suburb) setSuburb(draft.suburb);
                            if (draft.inspectionTime) setInspectionTime(draft.inspectionTime);

                            if (draft.itemResults && Array.isArray(draft.itemResults)) {
                                const draftAnswers: Record<number, string> = { ...defaultAns };
                                const draftComments: Record<number, string> = {};
                                draft.itemResults.forEach((result: any) => {
                                    if (result.templateItem && result.templateItem.id) {
                                        draftAnswers[result.templateItem.id] = result.answer || '';
                                        if (result.comments) {
                                            draftComments[result.templateItem.id] = result.comments;
                                        }
                                    }
                                });
                                setAnswers(draftAnswers);
                                setComments(draftComments);
                            } else {
                                setAnswers(defaultAns);
                            }

                            if (draft.photos && Array.isArray(draft.photos)) {
                                setExistingPhotos(draft.photos.map((p: any) => p.url));
                            }
                            loadedDraft = true;
                        } else {
                            toast.error('Draft not found.');
                        }
                    }

                    if (!loadedDraft) {
                        setAnswers(defaultAns);
                    }
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                toast.error('Failed to load asset or inspection templates');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [id, draftId]);

    // Suburb search over the bundled AU locality list: prefix matches first, then
    // substring matches. Filtering ~15k rows is sub-millisecond, so no debounce is
    // needed — suggestions appear as the driver types. Free text is always allowed.
    useEffect(() => {
        const query = suburb.trim().toLowerCase();
        if (query.length < 2) {
            setSuburbSuggestions([]);
            setSuburbSearching(false);
            return;
        }

        let active = true;
        setSuburbSearching(true);
        (async () => {
            try {
                const rows = await loadSuburbs();
                if (!active) return;

                const prefixMatches: { label: string; value: string }[] = [];
                const otherMatches: { label: string; value: string }[] = [];
                for (const [name, state, postcode] of rows) {
                    const haystack = name.toLowerCase();
                    if (haystack.startsWith(query)) {
                        prefixMatches.push({ label: `${name}, ${state}, ${postcode}`, value: name });
                        if (prefixMatches.length >= MAX_SUBURB_SUGGESTIONS) break;
                    } else if (otherMatches.length < MAX_SUBURB_SUGGESTIONS && haystack.includes(query)) {
                        otherMatches.push({ label: `${name}, ${state}, ${postcode}`, value: name });
                    }
                }

                const suggestions = [...prefixMatches, ...otherMatches].slice(0, MAX_SUBURB_SUGGESTIONS);
                setSuburbSuggestions(suggestions);
                setShowSuburbSuggestions(suggestions.length > 0);
            } catch (error) {
                if (active) {
                    setSuburbSuggestions([]);
                    setShowSuburbSuggestions(false);
                }
            } finally {
                if (active) setSuburbSearching(false);
            }
        })();

        return () => {
            active = false;
        };
    }, [suburb]);

    // Close the suburb suggestion list when clicking elsewhere on the page.
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (suburbBoxRef.current && !suburbBoxRef.current.contains(event.target as Node)) {
                setShowSuburbSuggestions(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    if (!ability.can(ACTIONS.CREATE, RESOURCES.ASSET_INSPECTION)) { 
        return <AccessRestricted />;
    }

    const drawSignature = (
        e: React.MouseEvent<HTMLCanvasElement> | React.TouchEvent<HTMLCanvasElement>,
        canvas: HTMLCanvasElement | null,
        isStart: boolean
    ) => {
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const rect = canvas.getBoundingClientRect();

        let clientX = 0;
        let clientY = 0;
        if ('touches' in e) {
            clientX = e.touches[0].clientX;
            clientY = e.touches[0].clientY;
        } else {
            clientX = e.clientX;
            clientY = e.clientY;
        }

        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;

        const x = (clientX - rect.left) * scaleX;
        const y = (clientY - rect.top) * scaleY;

        if (isStart) {
            ctx.beginPath();
            ctx.moveTo(x, y);
        } else {
            ctx.lineTo(x, y);
            ctx.stroke();
        }
    };

    const clearCanvas = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
        setSignatureData('');
    };

    const getSignatureData = () => {
        if (signatureType === 'Type') {
            return typedSignature.trim();
        } else if (canvasRef.current) {
            return canvasRef.current.toDataURL();
        }
        return '';
    };

    const handleAnswerChange = (itemId: number, value: string) => {
        setAnswers(prev => ({ ...prev, [itemId]: value }));
    };

    const handleCommentChange = (itemId: number, value: string) => {
        setComments(prev => ({ ...prev, [itemId]: value }));
    };

    const handleTemplateChange = async (templateId: string) => {
        const newId = parseInt(templateId);
        if (newId === selectedTemplateId) return;
        setSelectedTemplateId(newId);
        try {
            const templateDetailRes = await api.get<InspectionTemplate>(`/api/inspection-templates/${newId}`);
            setTemplate(templateDetailRes);
            // Reset answers and comments for new template items
            const defaultAns: Record<number, string> = {};
            templateDetailRes.items.forEach((item: any) => {
                defaultAns[item.id] = '';
            });
            setAnswers(defaultAns);
            setComments({});
        } catch (error) {
            console.error('Error fetching template:', error);
            toast.error('Failed to load selected template');
        }
    };

    // Calculate Summary Stats
    const totalQuestions = template?.items.length || 0;
    const answeredCount = Object.keys(answers).filter(k => answers[parseInt(k)] !== '').length;
    const yesCount = Object.values(answers).filter(v => v === 'Yes').length;
    const noCount = Object.values(answers).filter(v => v === 'No').length;
    const naCount = Object.values(answers).filter(v => v === 'N/A').length;
    const completionPercentage = totalQuestions === 0 ? 0 : Math.round((answeredCount / totalQuestions) * 100);

    const handleSubmit = async (e: React.FormEvent, isDraft: boolean) => {
        e.preventDefault();

        if (!asset || !template) return;

        if (!inspectorName.trim()) {
            toast.error('Inspector name is required');
            return;
        }

        const finalSignature = getSignatureData();
        if (!isDraft && !finalSignature) {
            toast.error('Signature is mandatory to complete the inspection');
            return;
        }

        // Validate mandatory questions
        if (!isDraft) {
            const missingMandatory = template.items.find(item => item.isMandatory && !answers[item.id]);
            if (missingMandatory) {
                toast.error(`Please answer all mandatory questions. Missing: "${missingMandatory.question}"`);
                return;
            }
        }

        try {
            setSubmitting(true);

            let uploadedPhotoUrls: string[] = [...existingPhotos];
            if (photos.length > 0) {
                const formData = new FormData();
                photos.forEach(p => formData.append('photos', p));
                const uploadRes = await api.post(`/api/assets/${asset.id}/inspections/upload-photos`, formData);
                if (uploadRes.urls) {
                    uploadedPhotoUrls = uploadRes.urls;
                }
            }

            const itemsPayload = template.items.map(item => ({
                templateItemId: item.id,
                answer: answers[item.id] || null,
                comments: comments[item.id] || ''
            }));

            const payload = {
                templateId: template.id,
                templateName: template.name,
                inspectionDate,
                inspectorName,
                inspectorSignature: finalSignature,
                status: isDraft ? 'Draft' : 'Completed',
                outcome,
                generalComments,
                suburb: suburb.trim(),
                inspectionTime,
                photos: uploadedPhotoUrls,
                items: itemsPayload,
                draftId: draftId ? parseInt(draftId) : undefined,
                isUpdate: !!draftId
            };

            await api.post(`/api/assets/${asset.id}/inspections`, payload);

            toast.success(isDraft ? 'Inspection draft saved' : 'Inspection completed successfully');
            navigate(`/assets/${asset.id}`);
        } catch (error: any) {
            console.error('Error saving inspection:', error);
            toast.error(error.response?.data?.message || 'Failed to save inspection');
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!asset || !template) {
        return (
            <div className="text-center py-20">
                <h2 className="text-2xl font-bold text-gray-900">No Inspection Template Found</h2>
                <p className="text-gray-500 mt-2">There is no active inspection template configured for asset type "{asset?.type}".</p>
                <Button onClick={() => navigate(-1)} className="mt-4">Go Back</Button>
            </div>
        );
    }

    return (
        <div className="max-w-[1000px] mx-auto pb-20 animate-in fade-in duration-500">
            {/* Header */}
            <div className="flex items-center gap-4 mb-4">
                <Button
                    variant="ghost"
                    size="icon"
                    className="rounded-xl border border-gray-100 bg-white hover:bg-gray-50"
                    onClick={() => navigate(-1)}
                >
                    <ArrowLeft size={20} />
                </Button>
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 tracking-tight flex items-center gap-2">
                        Inspection
                    </h1>
                    <p className="text-gray-500 text-sm font-medium mt-0.5">
                        Conduct a standardized inspection for {asset.name}
                    </p>
                </div>
            </div>

            {/* Banner */}
            <div className="bg-blue-50/50 border border-blue-100 rounded-2xl p-4 mb-8 flex items-start gap-4 shadow-sm">
                <div className="p-2 bg-blue-100 rounded-xl shrink-0 mt-0.5">
                    <CheckCircle2 size={18} className="text-blue-600" />
                </div>
                <div>
                    <h3 className="font-bold text-blue-900 text-sm mb-1">Standardized Inspection: {template.name}</h3>
                    <p className="text-blue-800 text-sm font-medium leading-relaxed">
                        {template.description || 'Answer the questions below to complete the inspection log.'}
                    </p>
                </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* Main Form */}
                <div className="lg:col-span-2 space-y-8">
                    <form onSubmit={(e) => handleSubmit(e, false)} className="space-y-8">

                        {/* 1. Asset & Inspector Details */}
                        <section className="bg-white p-6 md:p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                            <div className="flex items-center gap-3">
                                <Box size={24} className="text-blue-500" />
                                <div>
                                    <h2 className="font-bold text-gray-900 text-lg">Inspection Details</h2>
                                    <p className="text-gray-500 text-xs font-medium">Core information</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-6 bg-gray-50 p-6 rounded-2xl border border-gray-100 mb-6">
                                <div>
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Asset Name</span>
                                    <p className="font-bold text-gray-900">{asset.name} ({asset.tag})</p>
                                </div>
                                <div>
                                    <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest block mb-1">Asset Status</span>
                                    <p className="font-bold text-gray-900">{asset.status}</p>
                                </div>
                            </div>

                            {allTemplates.length > 0 && (
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">Inspection Template <span className="text-red-500">*</span></label>
                                    <Select
                                        value={selectedTemplateId?.toString() || ''}
                                        onValueChange={handleTemplateChange}
                                    >
                                        <SelectTrigger className="w-full rounded-xl h-12 px-4 border-gray-200 font-bold">
                                            <SelectValue placeholder="Select template..." />
                                        </SelectTrigger>
                                        <SelectContent className="rounded-xl">
                                            {allTemplates.map((t) => (
                                                <SelectItem key={t.id} value={t.id.toString()} className="font-bold">
                                                    {t.name}{t.isDefault ? ' (Default)' : ''}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">Inspection Date <span className="text-red-500">*</span></label>
                                    <Input
                                        type="date"
                                        value={inspectionDate}
                                        onChange={(e) => {
                                            setInspectionDate(e.target.value);
                                            // Selecting the date captures the completion time.
                                            setInspectionTime(currentTimeHHMM());
                                        }}
                                        className="rounded-xl h-12 px-4 border-gray-200"
                                        required
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">Time of Inspection Completion</label>
                                    <Input
                                        type="time"
                                        value={inspectionTime}
                                        onChange={(e) => setInspectionTime(e.target.value)}
                                        className="rounded-xl h-12 px-4 border-gray-200"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">Inspector Name <span className="text-red-500">*</span></label>
                                    <Input
                                        type="text"
                                        value={inspectorName}
                                        onChange={(e) => setInspectorName(e.target.value)}
                                        className="rounded-xl h-12 px-4 border-gray-200"
                                        placeholder="Enter your full name"
                                        required
                                    />
                                </div>
                                <div className="space-y-2 md:col-span-2" ref={suburbBoxRef}>
                                    <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">Location (Suburb)</label>
                                    <div className="relative">
                                        <Input
                                            type="text"
                                            value={suburb}
                                            onChange={(e) => {
                                                setSuburb(e.target.value);
                                                setShowSuburbSuggestions(true);
                                            }}
                                            onFocus={() => {
                                                // Lazy: the list is fetched only when the driver reaches this
                                                // field, so opening an inspection page that never searches the
                                                // suburb pays nothing. By the time a couple of characters are
                                                // typed the download is usually already done.
                                                loadSuburbs().catch(() => { /* the search effect surfaces load failures */ });
                                                setShowSuburbSuggestions(suburbSuggestions.length > 0);
                                            }}
                                            autoComplete="off"
                                            className="rounded-xl h-12 pl-10 pr-4 border-gray-200"
                                            placeholder="Search the suburb the load is delivered to..."
                                        />
                                        <svg
                                            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                                            xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                                            fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                                        >
                                            <circle cx="11" cy="11" r="8" />
                                            <path d="m21 21-4.3-4.3" />
                                        </svg>
                                        {suburbSearching && (
                                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-bold text-gray-400 uppercase tracking-widest">Searching</span>
                                        )}

                                        {showSuburbSuggestions && suburbSuggestions.length > 0 && (
                                            <ul className="absolute z-20 mt-1 w-full max-h-60 overflow-y-auto rounded-xl border border-gray-200 bg-white shadow-xl">
                                                {suburbSuggestions.map((s) => (
                                                    <li key={s.label}>
                                                        <button
                                                            type="button"
                                                            onClick={() => {
                                                                setSuburb(s.value);
                                                                setShowSuburbSuggestions(false);
                                                            }}
                                                            className="w-full text-left px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 border-b border-gray-50 last:border-b-0"
                                                        >
                                                            {s.label}
                                                        </button>
                                                    </li>
                                                ))}
                                            </ul>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </section>

                        {/* 2. Questions */}
                        <section className="bg-white p-6 md:p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                            <div className="flex items-center gap-3">
                                <FileText size={24} className="text-indigo-500" />
                                <div>
                                    <h2 className="font-bold text-gray-900 text-lg">Checklist</h2>
                                    <p className="text-gray-500 text-xs font-medium">Please answer all applicable tracking points</p>
                                </div>
                            </div>

                            <div className="space-y-4">
                                {template.items.sort((a, b) => a.listOrder - b.listOrder).map((item, index) => (
                                    <div key={item.id} className={`p-5 rounded-2xl border ${item.isMandatory ? 'bg-blue-50/40 border-blue-100' : 'bg-gray-50 border-gray-100'} transition-all`}>
                                        <div className="flex justify-between items-start gap-4 mb-4">
                                            <div>
                                                <h4 className="font-bold text-gray-900 text-sm">{index + 1}. {item.question}</h4>
                                                {item.isMandatory && <span className="text-[10px] font-bold text-blue-600 uppercase tracking-widest mt-1 block">Mandatory</span>}
                                            </div>
                                            <div className="shrink-0">
                                                <div className="flex bg-white rounded-lg border border-gray-200 p-1 shadow-sm overflow-hidden">
                                                    {['Yes', 'No', 'N/A'].map(opt => (
                                                        <button
                                                            key={opt}
                                                            type="button"
                                                            onClick={() => handleAnswerChange(item.id, opt)}
                                                            className={`px-3 py-1.5 text-xs font-bold rounded-md transition-all ${answers[item.id] === opt ?
                                                                (opt === 'Yes' ? 'bg-emerald-100 text-emerald-700' : opt === 'No' ? 'bg-red-100 text-red-700' : 'bg-gray-200 text-gray-700')
                                                                : 'text-gray-500 hover:bg-gray-50'}`}
                                                        >
                                                            {opt}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                        <div>
                                            <Input
                                                placeholder="Add comments / notes (optional)..."
                                                value={comments[item.id] || ''}
                                                onChange={(e) => handleCommentChange(item.id, e.target.value)}
                                                className={`h-9 text-xs border-0 bg-white/60 focus-visible:ring-1 shadow-sm ${item.isMandatory ? 'focus-visible:ring-blue-200' : 'focus-visible:ring-gray-200'}`}
                                            />
                                        </div>
                                    </div>
                                ))}
                                {template.items.length === 0 && (
                                    <div className="text-center py-6 bg-gray-50 rounded-2xl border border-gray-100">
                                        <p className="text-sm font-medium text-gray-500">No questions defined in this template.</p>
                                    </div>
                                )}
                            </div>
                        </section>

                        {/* 3. Photos Upload */}
                        <section className="bg-white p-6 md:p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-6">
                            <div className="flex items-center gap-3">
                                <ImageIcon size={24} className="text-gray-900" />
                                <div>
                                    <h2 className="font-bold text-gray-900 text-lg">Photos</h2>
                                    <p className="text-gray-500 text-xs font-medium">Add photos to document the inspection (optional)</p>
                                </div>
                            </div>

                            <div className="space-y-3">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={() => fileInputRef.current?.click()}
                                    className="w-full h-11 rounded-xl border-gray-200 text-gray-700 bg-white hover:bg-gray-50 font-bold gap-2"
                                >
                                    <Upload size={16} />
                                    Upload Photo
                                </Button>
                                <p className="text-xs text-gray-400 font-medium tracking-wide">Maximum file size: 5MB</p>
                                <input
                                    type="file"
                                    multiple
                                    ref={fileInputRef}
                                    onChange={handleFileSelect}
                                    accept="image/*"
                                    className="hidden"
                                />

                                {photos.length > 0 && (
                                    <div className="flex flex-wrap gap-4 mt-6 pt-2">
                                        {photos.map((photo, idx) => (
                                            <div key={idx} className="relative w-24 h-24 rounded-lg overflow-hidden border border-gray-200 group">
                                                <img
                                                    src={URL.createObjectURL(photo)}
                                                    alt="preview"
                                                    className="w-full h-full object-cover"
                                                    onLoad={(e) => URL.revokeObjectURL((e.target as HTMLImageElement).src)}
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => removePhoto(idx)}
                                                    className="absolute top-1 right-1 bg-white p-1 rounded-full shadow-sm hover:bg-gray-100 opacity-80 group-hover:opacity-100 transition-opacity"
                                                >
                                                    <X size={12} className="text-gray-900" />
                                                </button>
                                            </div>
                                        ))}

                                        {existingPhotos.map((url, idx) => (
                                            <div key={`existing-${idx}`} className="relative w-24 h-24 rounded-lg overflow-hidden border border-gray-200 group">
                                                <img
                                                    src={url}
                                                    alt="existing preview"
                                                    className="w-full h-full object-cover"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={() => removeExistingPhoto(idx)}
                                                    className="absolute top-1 right-1 bg-white p-1 rounded-full shadow-sm hover:bg-gray-100 opacity-80 group-hover:opacity-100 transition-opacity"
                                                >
                                                    <X size={12} className="text-gray-900" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </section>

                        {/* 4. Finalization & Signatures */}
                        <section className="bg-white p-6 md:p-8 rounded-[32px] border border-gray-100 shadow-sm space-y-8">
                            <div className="flex items-center gap-3 mb-6">
                                <Signature size={24} className="text-gray-900" />
                                <div>
                                    <h2 className="font-bold text-gray-900 text-lg">Finalize</h2>
                                    <p className="text-gray-500 text-xs font-medium">Outcome and signature</p>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">Inspection Outcome <span className="text-red-500">*</span></label>
                                    <Select value={outcome} onValueChange={(val: any) => setOutcome(val)}>
                                        <SelectTrigger className="w-full rounded-xl h-12 px-4 border-gray-200 font-bold">
                                            <SelectValue placeholder="Select outcome..." />
                                        </SelectTrigger>
                                        <SelectContent className="rounded-xl">
                                            <SelectItem value="Pass" className="font-bold text-emerald-700 focus:text-emerald-800">Pass</SelectItem>
                                            <SelectItem value="Pass with Notes" className="font-bold text-amber-600 focus:text-amber-700">Pass with Notes</SelectItem>
                                            <SelectItem value="Fail" className="font-bold text-red-600 focus:text-red-700">Fail</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div className="space-y-2">
                                    <label className="text-xs font-bold text-gray-700 uppercase tracking-wide">General Comments (Optional)</label>
                                    <Input
                                        placeholder="Summary notes..."
                                        value={generalComments}
                                        onChange={(e) => setGeneralComments(e.target.value)}
                                        className="rounded-xl h-12 px-4 border-gray-200"
                                    />
                                </div>
                            </div>

                            {/* Inspector Signature */}
                            <div className="space-y-4 pt-4">
                                <div className="flex items-center justify-between">
                                    <label className="text-xs font-bold text-gray-900 uppercase tracking-wide">Inspector Signature <span className="text-red-500">*</span></label>
                                    <div className="flex gap-2">
                                        <Button type="button" size="sm" variant={signatureType === 'Type' ? 'default' : 'outline'} onClick={() => setSignatureType('Type')} className="h-8 rounded-lg text-xs font-bold px-4">Type</Button>
                                        <Button type="button" size="sm" variant={signatureType === 'Draw' ? 'default' : 'outline'} onClick={() => setSignatureType('Draw')} className="h-8 rounded-lg text-xs font-bold px-4">Draw</Button>
                                    </div>
                                </div>
                                {signatureType === 'Type' ? (
                                    <Input
                                        placeholder="Type full name of inspector"
                                        value={typedSignature}
                                        onChange={(e) => setTypedSignature(e.target.value)}
                                        className="rounded-xl h-12 px-4 border-gray-200 font-[signature] text-xl tracking-wider text-gray-800 bg-gray-50"
                                    />
                                ) : (
                                    <div className="relative border border-gray-200 rounded-xl overflow-hidden bg-gray-50 touch-none">
                                        <canvas
                                            ref={canvasRef}
                                            width={800} height={200} className="w-full h-[160px] cursor-crosshair"
                                            onMouseDown={(e) => { setIsDrawing(true); drawSignature(e, canvasRef.current, true); }}
                                            onMouseMove={(e) => { if (isDrawing) drawSignature(e, canvasRef.current, false); }}
                                            onMouseUp={() => setIsDrawing(false)}
                                            onMouseLeave={() => setIsDrawing(false)}
                                            onTouchStart={(e) => { setIsDrawing(true); drawSignature(e, canvasRef.current, true); }}
                                            onTouchMove={(e) => { if (isDrawing) drawSignature(e, canvasRef.current, false); }}
                                            onTouchEnd={() => setIsDrawing(false)}
                                        />
                                        <Button type="button" variant="ghost" size="sm" onClick={() => clearCanvas()} className="absolute top-2 right-2 h-8 rounded-lg text-xs font-bold text-gray-500 hover:text-red-500 bg-white shadow-sm border border-gray-100">Clear</Button>
                                    </div>
                                )}
                            </div>
                        </section>

                        <div className="flex flex-col-reverse sm:flex-row justify-between items-center pt-4 gap-4">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={(e) => handleSubmit(e, true)}
                                className="w-full sm:w-auto h-14 rounded-2xl px-6 font-bold text-gray-600 bg-white hover:bg-gray-50 border-gray-200"
                                disabled={submitting}
                            >
                                <Save size={18} className="mr-2" />
                                Save Draft
                            </Button>

                            <div className="flex flex-col-reverse sm:flex-row gap-4 w-full sm:w-auto">
                                <Button
                                    type="button"
                                    variant="ghost"
                                    onClick={() => navigate(-1)}
                                    className="w-full sm:w-auto h-14 rounded-2xl px-6 font-bold text-gray-500 hover:text-gray-700"
                                    disabled={submitting}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    type="submit"
                                    className="w-full sm:w-auto h-14 rounded-2xl px-8 font-bold bg-blue-600 hover:bg-blue-700 text-white shadow-xl shadow-blue-600/20"
                                    disabled={submitting}
                                >
                                    {submitting ? 'Processing...' : (
                                        <span className="flex items-center justify-center gap-2">
                                            <CheckCircle2 size={20} />
                                            Complete Inspection
                                        </span>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </form>
                </div>

                {/* Info Sidebar */}
                <div className="hidden lg:block lg:col-span-1">
                    <div className="sticky top-24 space-y-6">
                        <div className="bg-gray-50/50 p-6 rounded-3xl border border-gray-100">
                            <div className="flex items-center gap-3 mb-4">
                                <div className="w-10 h-10 rounded-xl bg-indigo-50 flex items-center justify-center text-indigo-500">
                                    <FileText size={20} />
                                </div>
                                <h3 className="font-bold text-gray-900 text-sm">Inspection Tips</h3>
                            </div>
                            <div className="space-y-4 text-sm font-medium text-gray-600">
                                <p>
                                    Follow the standardized checklist to ensure regulatory compliance and asset tracking.
                                </p>
                                <ul className="space-y-3 pt-2 border-t border-gray-200/60">
                                    <li className="flex items-start gap-2">
                                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 shrink-0" />
                                        <span>Mandatory questions must be completed.</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500 mt-1.5 shrink-0" />
                                        <span>Include notes for issues you uncover to track required maintenance.</span>
                                    </li>
                                </ul>
                            </div>
                        </div>

                        {/* Inspection Summary */}
                        <div className="bg-white p-6 rounded-[32px] border border-gray-100 shadow-xl shadow-gray-200/50 space-y-6">
                            <h3 className="font-bold text-gray-900 uppercase tracking-widest text-xs flex items-center gap-2">
                                <Box size={16} className="text-blue-500" />
                                Inspection Summary
                            </h3>

                            <div className="space-y-2">
                                <div className="flex justify-between items-center text-xs font-bold text-gray-600">
                                    <span>Progress</span>
                                    <span>{answeredCount} / {totalQuestions} ({completionPercentage}%)</span>
                                </div>
                                <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                                    <div
                                        className="bg-blue-600 h-2 rounded-full transition-all duration-500"
                                        style={{ width: `${completionPercentage}%` }}
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div className="p-4 bg-emerald-50/50 rounded-2xl border border-emerald-100 flex flex-col items-center justify-center text-center">
                                    <span className="text-2xl font-bold text-emerald-600">{yesCount}</span>
                                    <span className="text-[10px] font-bold text-emerald-600/70 uppercase tracking-wider mt-1">Yes</span>
                                </div>
                                <div className="p-4 bg-red-50/50 rounded-2xl border border-red-100 flex flex-col items-center justify-center text-center">
                                    <span className="text-2xl font-bold text-red-600">{noCount}</span>
                                    <span className="text-[10px] font-bold text-red-600/70 uppercase tracking-wider mt-1">No</span>
                                </div>
                                <div className="col-span-2 grid grid-cols-2 gap-3">
                                    <div className="p-4 bg-gray-50 rounded-2xl border border-gray-100 flex flex-col items-center justify-center text-center">
                                        <span className="text-xl font-bold text-gray-600">{naCount}</span>
                                        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mt-1">N/A</span>
                                    </div>
                                    <div className="p-4 bg-blue-50/50 rounded-2xl border border-blue-100 flex flex-col items-center justify-center text-center">
                                        <span className="text-xl font-bold text-blue-600">{completionPercentage}%</span>
                                        <span className="text-[10px] font-bold text-blue-600/70 uppercase tracking-wider mt-1">Complete</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default NewInspection;
